/**
 * Authentication-aware retry configuration for TanStack Query
 *
 * This utility provides retry logic that respects authentication state
 * and prevents infinite retries when authentication is blocked.
 */

import { logger } from "@/lib/logger";

export interface AuthAwareRetryOptions {
  maxRetries?: number;
  retryDelay?: (attemptIndex: number) => number;
  skipAuthErrors?: boolean;
}

/**
 * Default retry configuration that respects authentication
 */
export const defaultAuthAwareRetry = (options: AuthAwareRetryOptions = {}) => {
  const {
    maxRetries = 1,
    retryDelay = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    skipAuthErrors = true,
  } = options;

  return (failureCount: number, error: any) => {
    logger.info("🔄 TanStack Query Retry Check:", {
      failureCount,
      maxRetries,
      errorMessage: error?.message,
      errorCode: error?.code,
      errorType: typeof error,
      skipAuthErrors,
    });

    // Don't retry if the error is authentication-related
    if (
      skipAuthErrors &&
      (error?.message?.includes("User not authenticated") ||
        error?.message?.includes("Authentication still loading") ||
        error?.code === "AUTH_REQUIRED" ||
        error?.code === "AUTH_LOADING")
    ) {
      logger.info("🚫 TanStack Query: Skipping retry due to authentication error:", {
        message: error.message,
        code: error.code,
        failureCount,
        willRetry: false,
      });
      return false;
    }

    // For other errors, retry up to maxRetries times
    const willRetry = failureCount < maxRetries;
    logger.info("✅ TanStack Query: Will retry:", {
      failureCount,
      maxRetries,
      willRetry,
      errorMessage: error?.message,
    });
    return willRetry;
  };
};

/**
 * Retry configuration for queries (default behavior)
 */
export const queryRetry = defaultAuthAwareRetry({ maxRetries: 1 });

/**
 * Retry configuration for mutations (more conservative)
 */
export const mutationRetry = defaultAuthAwareRetry({ maxRetries: 1 });

/**
 * Retry configuration for critical operations (more aggressive)
 */
export const criticalRetry = defaultAuthAwareRetry({ maxRetries: 3 });

/**
 * Retry configuration that never retries (for one-shot operations)
 */
export const noRetry = defaultAuthAwareRetry({ maxRetries: 0 });

/**
 * Retry delay function with exponential backoff
 */
export const exponentialBackoff = (attemptIndex: number) =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

/**
 * Retry delay function with linear backoff
 */
export const linearBackoff = (attemptIndex: number) => Math.min(1000 * (attemptIndex + 1), 10000);
